/* eslint-disable no-useless-catch */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 06 2023
 **/

import isEqual from "lodash-es/isEqual";
import { atom, useAtom } from "jotai";
import { useAsyncCallback } from "react-async-hook";
import type { AsyncStateStatus } from "react-async-hook";

import { RestAPIError } from "@/frameworks/libraries/restservice";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { FileAPI as FileBoxAPI } from "@/frameworks/core/API";
import { FileAPI } from "./private";

const fileAPI = new FileAPI();
const fileBoxAPI = new FileBoxAPI();
const folderDataAtom = atom<{ timestamp: number } & Partial<SwaggerFileAPIModels.ApiChatSimpleRoomIdResponse>>({
	timestamp: 0
});
export type RelativeFolderType = {
	id: string;
	folderType: "CHAT" | "SPACE" | "INDIVIDUAL" | "ALL";
	referenceType?: SwaggerFileAPIModels.ReferenceType;
	referenceID?: string;
	name?: string;
	isMyFile: boolean;
	all?: boolean;
	parent?: RelativeFolderType;
	myRoomOnly?: boolean;
};

const currentFolderAtom = atom<RelativeFolderType>({
	id: "",
	name: "",
	isMyFile: false,
	referenceType: "INDIVIDUAL",
	referenceID: "",
	folderType: "INDIVIDUAL",
	myRoomOnly: false
});
const myRoomIDAtom = atom<string>("");

type FileManagerProps = Partial<{
	onFolderChanged: (id: string) => void;
	onMyFolders: (list: FileBox.Folders.GetMyFoldersResponse) => void;
}>;

export default function useFileManager(props?: FileManagerProps) {
	const [folderData, setFolderData] = useAtom(folderDataAtom);
	const [currentFolder, setCurrentFolder] = useAtom(currentFolderAtom);
	const [myRoomID, setMyRoomID] = useAtom(myRoomIDAtom);

	const fn = {
		mount() {
			try {
				const is_refresh_root =
					folderData.timestamp === 0 || new Date(Date.now() - folderData.timestamp).getMinutes() >= 5; // 5분에 한번씩
				if (Configure.is_authenticated && is_refresh_root) {
					console.info("#useFileManager# mouted and get all side folders");
					api.get_all_folders.callback.execute();
				}
			} catch (error: any) {
				console.warn("#useFileManager# mount failed", error);
			}
		},
		common_process({ status, error }: { status: AsyncStateStatus; error: Error | undefined }) {
			switch (status) {
				case "success": {
					return true;
				}

				case "loading": {
					// console.debug("#useFileManager# loading...");
					break;
				}

				case "error": {
					const _error = error as RestAPIError;
					console.warn("#useFileManager# API error", _error?.code, _error?.message, _error);
					break;
				}
			}
			return false;
		}
	};
	const event = {
		onFolderChanged() {
			currentFolder.id.length >= 0 && props?.onFolderChanged?.(currentFolder.id);
			console.debug("#useFileManager# on folder changed", currentFolder);
		}
	};
	const api = {
		get_all_folders: {
			callback: useAsyncCallback(fileAPI.get_all_folders),
			process() {
				if (fn.common_process(api.get_all_folders.callback)) {
					const { result } = api.get_all_folders.callback;
					if (result) {
						const { timestamp, ...rest } = folderData;
						if (isEqual(result.data, rest) === false) {
							setFolderData({ timestamp: Date.now(), ...result.data });
							currentFolder.id.length <= 0 &&
								setCurrentFolder({
									id: result.data.myRoomId ?? "",
									name: _T("내 파일"),
									isMyFile: true,
									referenceType: "INDIVIDUAL",
									referenceID: myRoomID,
									folderType: "INDIVIDUAL"
								});
							console.debug(
								"#useFileManager# get folder list from server, set current folder",
								result.data,
								result.data.myRoomId
							);
						}
					}
				}
			}
		},
		get_my_folders: {
			callback: useAsyncCallback(fileAPI.get_my_folders),
			process() {
				if (fn.common_process(api.get_my_folders.callback)) {
					const { result } = api.get_my_folders.callback;
					if (result) {
						props?.onMyFolders?.(result?.data);
					}
				}
			}
		},
		get_folder_file_list: {
			callback: useAsyncCallback(fileAPI.get_folder_file_list),
			process() {
				if (fn.common_process(api.get_folder_file_list.callback)) {
					const { result } = api.get_folder_file_list.callback;
					if (result) {
					}
				}
			}
		},
		get_all_folder_file_list: {
			callback: useAsyncCallback(fileAPI.get_all_folder_file_list),
			process() {
				if (fn.common_process(api.get_all_folder_file_list.callback)) {
					const { result } = api.get_all_folder_file_list.callback;
					if (result) {
					}
				}
			}
		},
		create_folder: {
			callback: useAsyncCallback(fileAPI.create_folder),
			process() {
				if (fn.common_process(api.create_folder.callback)) {
					const { result } = api.create_folder.callback;
					if (result) {
					}
				}
			}
		},
		rename_folder: {
			callback: useAsyncCallback(fileAPI.rename_folder),
			process() {
				if (fn.common_process(api.rename_folder.callback)) {
					const { result } = api.rename_folder.callback;
					if (result) {
					}
				}
			}
		},
		delete_folder: {
			callback: useAsyncCallback(fileAPI.delete_folder),
			process() {
				if (fn.common_process(api.delete_folder.callback)) {
					const { result } = api.delete_folder.callback;
					if (result) {
					}
				}
			}
		},
		move_folder: {
			callback: useAsyncCallback(fileAPI.move_folder),
			process() {
				if (fn.common_process(api.move_folder.callback)) {
					const { result } = api.move_folder.callback;
					if (result) {
					}
				}
			}
		},
		rename_file: {
			callback: useAsyncCallback(fileAPI.rename_file),
			process() {
				if (fn.common_process(api.rename_file.callback)) {
					const { result } = api.rename_file.callback;
					if (result) {
					}
				}
			}
		},
		copy_file: {
			callback: useAsyncCallback(fileAPI.copy_file),
			process() {
				if (fn.common_process(api.copy_file.callback)) {
					const { result } = api.copy_file.callback;
					if (result) {
					}
				}
			}
		},
		delete_file: {
			callback: useAsyncCallback(fileAPI.delete_file),
			process() {
				if (fn.common_process(api.delete_file.callback)) {
					const { result } = api.delete_file.callback;
					if (result) {
					}
				}
			}
		},
		upload_file: {
			callback: useAsyncCallback(fileAPI.upload_file),
			process() {
				if (fn.common_process(api.upload_file.callback)) {
					const { result } = api.upload_file.callback;
					if (result) {
					}
				}
			}
		},
		abort_file: {
			callback: useAsyncCallback(fileAPI.tus_abort),
			process() {
				if (fn.common_process(api.abort_file.callback)) {
					const { result } = api.abort_file.callback;
					if (result) {
					}
				}
			}
		},
		save_link: {
			callback: useAsyncCallback(fileAPI.save_link),
			process() {
				if (fn.common_process(api.save_link.callback)) {
					const { result } = api.save_link.callback;
					if (result) {
					}
				}
			}
		},
		remove_link: {
			callback: useAsyncCallback(fileAPI.remove_link),
			process() {
				if (fn.common_process(api.remove_link.callback)) {
					const { result } = api.remove_link.callback;
					if (result) {
					}
				}
			}
		},
		copy_link: {
			callback: useAsyncCallback(fileAPI.copy_link),
			process() {
				if (fn.common_process(api.copy_link.callback)) {
					const { result } = api.copy_link.callback;
					if (result) {
					}
				}
			}
		},
		download_files: {
			callback: useAsyncCallback(fileAPI.download_files),
			process() {
				if (fn.common_process(api.download_files.callback)) {
					const { result } = api.download_files.callback;
					if (result) {
					}
				}
			}
		},
		move_file: {
			callback: useAsyncCallback(fileAPI.move_file),
			process() {
				if (fn.common_process(api.move_file.callback)) {
					const { result } = api.move_file.callback;
					if (result) {
					}
				}
			}
		},
		download_folder: {
			callback: useAsyncCallback(fileAPI.download_folder),
			process() {
				if (fn.common_process(api.download_folder.callback)) {
					const { result } = api.download_folder.callback;
					if (result) {
					}
				}
			}
		},
		convert_doc_to_image: {
			callback: useAsyncCallback(fileAPI.convert_doc_to_image),
			process() {
				if (fn.common_process(api.convert_doc_to_image.callback)) {
					const { result } = api.convert_doc_to_image.callback;
					if (result) {
					}
				}
			}
		},
		download_doc_to_image: {
			callback: useAsyncCallback(fileAPI.download_doc_to_image),
			process() {
				if (fn.common_process(api.download_doc_to_image.callback)) {
					const { result } = api.download_doc_to_image.callback;
					if (result) {
					}
				}
			}
		},
		download_doc_thumbnail: {
			callback: useAsyncCallback(fileAPI.download_doc_thumbnail),
			process() {
				if (fn.common_process(api.download_doc_thumbnail.callback)) {
					const { result } = api.download_doc_thumbnail.callback;
					if (result) {
					}
				}
			}
		},
		progress_doc_image: {
			callback: useAsyncCallback(fileAPI.progress_doc_image),
			process() {
				if (fn.common_process(api.progress_doc_image.callback)) {
					const { result } = api.progress_doc_image.callback;
					if (result) {
					}
				}
			}
		},
		get_all_space_files: {
			callback: useAsyncCallback(fileAPI.get_all_space_files),
			process() {
				if (fn.common_process(api.get_all_space_files.callback)) {
					const { result } = api.get_all_space_files.callback;
					if (result) {
					}
				}
			}
		},
		get_all_dm_files: {
			callback: useAsyncCallback(fileAPI.get_all_dm_files),
			process() {
				if (fn.common_process(api.get_all_dm_files.callback)) {
					const { result } = api.get_all_dm_files.callback;
					if (result) {
					}
				}
			}
		},
		get_files_in_conference: {
			callback: useAsyncCallback(fileBoxAPI.get_file_list_in_conference),
			process() {
				if (fn.common_process(api.get_files_in_conference.callback)) {
					const { result } = api.get_files_in_conference.callback;
					if (result) {
					}
				}
			}
		}
	};

	const result = {
		folderData,
		currentFolder,
		myRoomID,
		setCurrentFolder,
		async getAllFolders() {
			const result = (await api.get_all_folders.callback.execute()).data;
			setMyRoomID(result.myRoomId ?? "");
			return result;
		},
		async getMyFolders(options?: FileBox.Folders.GetMyFoldersRequest) {
			if (options === undefined) {
				return (await api.get_my_folders.callback.execute()).data;
			} else {
				try {
					const result = await api.get_my_folders.callback.execute(options);
					return result.data;
				} catch (error: any) {
					throw error;
				}
			}
		},
		async getFolderFileList(options: FileBox.Folders.GetFolderFileListRequest) {
			return (await api.get_folder_file_list.callback.execute(options)).data;
		},
		async getAllFolderFileList(options: FileBox.Folders.GetAllFolderFileRequest) {
			return (await api.get_all_folder_file_list.callback.execute(options)).data;
		},
		async createFolder(options: FileBox.Folders.CreateFolderRequest) {
			return (await api.create_folder.callback.execute(options)).data;
		},
		async renameFolder(options: FileBox.Folders.RenameFolderRequest) {
			return (await api.rename_folder.callback.execute(options)).data;
		},
		async deleteFolder(options: FileBox.Folders.DeleteFolderRequest) {
			return (await api.delete_folder.callback.execute(options)).data;
		},
		async moveFolder(options: FileBox.Folders.MoveFolderRequest) {
			return (await api.move_folder.callback.execute(options)).data;
		},
		async downloadFolder(options: FileBox.Folders.DownloadFolderRequest) {
			return (await api.download_folder.callback.execute(options)).data;
		},
		async isExistsFilename(options: FileBox.Files.ExistFilenameRequest) {
			try {
				const result = await fileAPI.exists_filename(options);
				return result.data;
			} catch (error: any) {
				throw error;
			}
		},
		async uploadFile(options: FileBox.Upload.UploadRequest) {
			return (await api.upload_file.callback.execute(options)).data;
		},
		async renameFile(options: FileBox.Files.RenameFileRequest) {
			return (await api.rename_file.callback.execute(options)).data;
		},
		async deleteFile(options: FileBox.Files.DeleteFileRequest) {
			return (await api.delete_file.callback.execute(options)).data;
		},
		async abortFile(options: FileBox.Tus.AbortRequest) {
			return (await api.abort_file.callback.execute(options)).data;
		},
		async moveFile(options: FileBox.Files.FileMoveRequest) {
			return (await api.move_file.callback.execute(options)).data;
		},
		async saveLink(options: FileBox.Link.SaveLinkRequest) {
			return (await api.save_link.callback.execute(options)).data;
		},
		async removeLink(options: FileBox.Link.RemoveLinkRequest) {
			return (await api.remove_link.callback.execute(options)).data;
		},
		async copyLink(options: FileBox.Link.CopyLinkRequest) {
			return (await api.copy_link.callback.execute(options)).data;
		},
		async downloadFiles(options: FileBox.Files.DownloadFilesRequest) {
			return (await api.download_files.callback.execute(options)).data;
		},
		async copyFile(options: FileBox.Files.CopyFileRequest) {
			return (await api.copy_file.callback.execute(options)).data;
		},
		async convertDocToImage(options: FileBox.DocImage.ConvertDocToImageRequest) {
			return (await api.convert_doc_to_image.callback.execute(options)).data;
		},
		async downloadDocToImage(options: FileBox.DocImage.DownloadDocImageRequest) {
			return (await api.download_doc_to_image.callback.execute(options)).data;
		},
		async downloadDocThumbnail(options: FileBox.DocImage.DownloadDocThumbnailRequest) {
			return (await api.download_doc_thumbnail.callback.execute(options)).data;
		},
		async progressDocImage(options: FileBox.DocImage.ProgressDocImageRequest) {
			return (await api.progress_doc_image.callback.execute(options)).data;
		},
		async getAllSpaceFiles(options: FileBox.Folders.GetAllSpaceFileRequest) {
			return (await api.get_all_space_files.callback.execute(options)).data;
		},
		async getAllDMFiles(options: FileBox.Folders.GetAllDMFileRequest) {
			return (await api.get_all_dm_files.callback.execute(options)).data;
		},
		async getFilesInConference(id: string) {
			return (await api.get_files_in_conference.callback.execute({ id })).data;
		}
	};

	return result;
}
